import React, { useEffect, useState, useContext, useRef } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Link from 'next/link';
import { Container, Box, Typography, Drawer } from '@mui/material';
import { SET_CATEGORIES, SET_ALERT } from 'context/actions/system';
import { logoutAccount } from 'context/actions/auth';
import { GlobalContext } from 'context';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import useWindowSize from 'utils/useWindowSize';
import MenuDrawerItem from './MenuDrawerItem';
import LanguageChanger from './LanguageChanger';
import MenuDrawerSubItem from './MenuDrawerSubItem';
import MenuItem from './MenuItem';
import MenuSubItem from './MenuSubItem';
import { X, Menu as MenuIcon, ChevronLeft, Search } from 'react-feather';
import { getAdsList } from 'context/actions/ads';
import HeaderIconGroup from './HeaderIconGroup';

function useOutsideClick(ref, secondRef, callback) {
    useEffect(() => {
        function handleClickOutside(event) {
            if (
                ref.current &&
                !ref.current.contains(event.target) &&
                (!secondRef || (secondRef.current && !secondRef.current.contains(event.target)))
            ) {
                callback();
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [ref, callback]);
}

const Header = () => {
    const { sizes } = useWindowSize();
    const { t } = useTranslation();
    const router = useRouter();

    const menuRef = useRef();
    const mainMenuRef = useRef();
    const headerRef = useRef();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [fadeClass, setFadeClass] = useState('opacity-100');
    const { state, dispatch } = useContext(GlobalContext);
    const [showMenu, setShowMenu] = useState(false);
    const [showSubMenu, setShowSubMenu] = useState(false);
    const [subMenu, setSubMenu] = useState(null);
    const [subMenuDirection, setSubMenuDirection] = useState('column');
    const [subMenuLeft, setSubMenuLeft] = useState(0);
    const [searchValue, setSearchValue] = useState('');
    const [ads, setAds] = useState([]);
    const [chosedLang, setChoosedLang] = useState(null);
    const [isLoggedIn, setLoggedIn] = useState(false);
    const [showSearch, setShowSearch] = useState(false);

    const productMenus = {
        names: [
            { langCode: 'mn', name: 'Бүтээгдэхүүн' },
            { langCode: 'en', name: 'Products' },
        ],
        url: 'products',
        children: state.system.categories,
    };

    useEffect(() => {
        const interval = setInterval(() => {
            setFadeClass('opacity-0');
            setTimeout(() => {
                setCurrentIndex((prev) => prev + 1);
                setFadeClass('opacity-100');
            }, 500);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        if (currentIndex === ads.length) setCurrentIndex(0);
    }, [currentIndex, ads]);

    useEffect(() => {
        setLoggedIn(!!state.auth?.accountLoggedIn);
    }, [state.auth]);

    useEffect(() => {
        getDatas();
    }, []);

    useEffect(() => {
        if (state.system.languages?.length) {
            state.system.languages.forEach((lng) => {
                if (lng.code === state.system.lang) setChoosedLang(lng);
            });
        }
    }, [state.system.languages]);

    const getDatas = async () => {
        const resAds = await getAdsList('&isActive=1');
        if (resAds.status === 'success') setAds(resAds.data);
    };

    const alertLogin = () =>
        dispatch({
            type: SET_ALERT,
            data: { isShow: true, type: 'warning', message: 'Нэвтэрч орно уу' },
        });

    useOutsideClick(menuRef, mainMenuRef, () => {
        setShowSubMenu(false);
        setSubMenu(null);
    });

    const handleMenuButtonClick = () => setShowMenu((prev) => !prev);

    const handleDrawerClose = (force) => {
        if (force === true) {
            setShowMenu(false);
            setShowSubMenu(false);
        } else if (showSubMenu) {
            setShowSubMenu(false);
        } else {
            setShowMenu(false);
            setShowSubMenu(false);
        }
    };

    const handleSearchSubmit = (e) => {
        e?.preventDefault();
        const q = searchValue.trim();
        if (!q) return;
        setShowSearch(false);
        setSearchValue('');
        router.push({ pathname: '/search', query: { q } });
    };

    const handleRoteClick = (menu) => {
        showMenu && handleDrawerClose(true);
        router.push(menu.url ? '/' + menu.url : '/content/' + menu.id);
    };

    const safeAdIndex = currentIndex >= ads.length ? 0 : currentIndex;

    return (
        <>
            {/* ── Sticky header wrapper ── */}
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    background: 'hsl(var(--background))',
                }}
            >
                {/* ── Дээд шар баннер ── */}
                <Container maxWidth={false} disableGutters={true}>
                    <Container
                        maxWidth={false}
                        disableGutters={true}
                        className="flex bg-tertiary-20 h-9 justify-center"
                    >
                        <Container
                            maxWidth="lg"
                            className="flex flex-row items-center justify-center relative px-4"
                        >
                            {ads?.length > 0 && ads[safeAdIndex] && (
                                <span
                                    className={`text-primary-950 text-[13px] transition-opacity duration-500 ${fadeClass}`}
                                >
                                    {
                                        ads[safeAdIndex].names.find(
                                            (item) => item.langCode === i18n.language
                                        )?.name
                                    }
                                </span>
                            )}
                            <Box className="absolute right-4 flex flex-row items-center">
                                {!sizes.sm && chosedLang && (
                                    <LanguageChanger
                                        languages={state.system.languages}
                                        chosedLang={chosedLang}
                                        setChoosedLang={setChoosedLang}
                                        dispatch={dispatch}
                                    />
                                )}
                                {isLoggedIn ? (
                                    <Typography
                                        variant="body13"
                                        className="!ml-4 !mr-0 text-secondary-500 cursor-pointer hover:text-secondary-600"
                                        onClick={() => {
                                            logoutAccount(dispatch);
                                            router.push('/');
                                        }}
                                    >
                                        {t('button.logout')}
                                    </Typography>
                                ) : (
                                    <Typography
                                        variant="body13"
                                        className="!ml-4 !mr-0 text-secondary-500 cursor-pointer hover:text-secondary-600"
                                        onClick={() => router.push('/login')}
                                    >
                                        {t('button.login')}
                                    </Typography>
                                )}
                            </Box>
                        </Container>
                    </Container>
                </Container>

                {/* ── Үндсэн header ── */}
                <Container
                    ref={headerRef}
                    maxWidth={false}
                    disableGutters={true}
                    className="border-b border-b-gray-100"
                >
                    <Container
                        maxWidth="lg"
                        className="flex flex-row items-center"
                        style={{
                            position: 'relative',
                            paddingTop: sizes.sm ? 32 : 20,
                            paddingBottom: sizes.sm ? 32 : 20,
                            paddingLeft: sizes.sm ? 16 : 24,
                            paddingRight: sizes.sm ? 16 : 24,
                        }}
                    >
                        {sizes.sm ? (
                            /* ── Mobile ── */
                            <>
                                {/* Зүүн: Menu + Хэл */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        flexShrink: 0,
                                    }}
                                >
                                    <MenuIcon
                                        color="#000"
                                        size={20}
                                        onClick={handleMenuButtonClick}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    {chosedLang && (
                                        <LanguageChanger
                                            languages={state.system.languages}
                                            chosedLang={chosedLang}
                                            setChoosedLang={setChoosedLang}
                                            dispatch={dispatch}
                                        />
                                    )}
                                </Box>
                                {/* Дунд: Лого (абсолют) */}
                                <Link
                                    href="/"
                                    passHref
                                    style={{
                                        position: 'absolute',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                    }}
                                >
                                    <Image
                                        width={120}
                                        height={32}
                                        src={
                                            i18n.language === 'mn'
                                                ? '/LogoFull.png'
                                                : '/LogoFullEng.png'
                                        }
                                        alt="Gorgeous logo"
                                        style={{ objectFit: 'contain' }}
                                    />
                                </Link>
                                {/* Баруун: Icon-ууд */}
                                <Box sx={{ ml: 'auto', flexShrink: 0 }}>
                                    <HeaderIconGroup
                                        isMobile={true}
                                        auth={state.auth}
                                        onSearch={() => {
                                            handleDrawerClose(true);
                                            setShowSearch(true);
                                        }}
                                        onBasket={() => {
                                            if (state.auth?.accountLoggedIn) {
                                                handleDrawerClose(true);
                                                router.push('/basket');
                                            } else alertLogin();
                                        }}
                                        onProfile={() => {
                                            handleDrawerClose(true);
                                            if (state.auth?.accountLoggedIn)
                                                router.push('/profile');
                                            else router.push('/login');
                                        }}
                                    />
                                </Box>
                            </>
                        ) : (
                            /* ── Desktop ── */
                            <>
                                {/* Лого */}
                                <Link href="/" passHref className="!mr-auto">
                                    <Box>
                                        <Image
                                            width={200}
                                            height={60}
                                            src={
                                                i18n.language === 'mn'
                                                    ? '/LogoFull.png'
                                                    : '/LogoFullEng.png'
                                            }
                                            alt="Gorgeous logo"
                                            style={{ objectFit: 'contain' }}
                                        />
                                    </Box>
                                </Link>
                                {/* Цэс */}
                                <Box ref={mainMenuRef} style={{ position: 'relative' }}>
                                    <ul
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            margin: 0,
                                            padding: 0,
                                            listStyle: 'none',
                                        }}
                                    >
                                        {state.system.userMenus?.length > 0 && (
                                            <MenuItem
                                                menu={productMenus}
                                                isActive={showSubMenu && subMenu === productMenus}
                                                onClose={() => {
                                                    setShowSubMenu(false);
                                                    setSubMenu(null);
                                                }}
                                                onClick={(e) => {
                                                    const li = e.currentTarget;
                                                    setSubMenuLeft(li.offsetLeft);
                                                    setSubMenuDirection('row');
                                                    setSubMenu(productMenus);
                                                    setShowSubMenu(true);
                                                }}
                                            />
                                        )}
                                        {state.system.userMenus?.map((m, ind) => (
                                            <MenuItem
                                                key={ind}
                                                menu={m}
                                                isActive={showSubMenu && subMenu?.id === m.id}
                                                onClose={() => {
                                                    setShowSubMenu(false);
                                                    setSubMenu(null);
                                                }}
                                                onClick={(e) => {
                                                    if (!m.children?.length) {
                                                        setShowSubMenu(false);
                                                        setSubMenu(null);
                                                        return;
                                                    }
                                                    const li = e.currentTarget;
                                                    setSubMenuLeft(li.offsetLeft);
                                                    setSubMenuDirection('column');
                                                    setSubMenu(m);
                                                    setShowSubMenu(true);
                                                }}
                                            />
                                        ))}
                                    </ul>
                                    {/* ── Desktop sub цэс ── */}
                                    <style>{`
                                    @keyframes subMenuIn { from { opacity:0; transform:translateY(-4px) } to { opacity:1; transform:translateY(0) } }
                                `}</style>
                                    {showSubMenu && subMenu && (
                                        <div
                                            ref={menuRef}
                                            style={{
                                                position: 'fixed',
                                                top: headerRef.current
                                                    ? headerRef.current.getBoundingClientRect()
                                                          .bottom
                                                    : 0,
                                                left: 0,
                                                right: 0,
                                                zIndex: 50,
                                                background: 'hsl(var(--background))',
                                                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                                animation: 'subMenuIn .16s ease',
                                            }}
                                        >
                                            {subMenu?.children?.length > 0 && (
                                                <ul
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: subMenuDirection,
                                                        flexWrap: 'wrap',
                                                        gap: '2px 4px',
                                                        padding: '8px 0',
                                                        margin: 0,
                                                        paddingLeft: mainMenuRef.current
                                                            ? subMenuLeft +
                                                              mainMenuRef.current.getBoundingClientRect()
                                                                  .left -
                                                              10
                                                            : subMenuLeft,
                                                    }}
                                                >
                                                    {subMenu.children.map((m, ind) => (
                                                        <MenuSubItem
                                                            key={ind}
                                                            menu={m}
                                                            onClick={() => {
                                                                setShowSubMenu(false);
                                                                setSubMenu(null);
                                                            }}
                                                        />
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    )}
                                </Box>
                                {/* Icon-ууд */}
                                <Box sx={{ ml: 'auto' }}>
                                    <HeaderIconGroup
                                        isMobile={false}
                                        auth={state.auth}
                                        onSearch={() => setShowSearch(true)}
                                        onBasket={() => {
                                            if (state.auth?.accountLoggedIn) router.push('/basket');
                                            else alertLogin();
                                        }}
                                        onProfile={() => {
                                            if (state.auth?.accountLoggedIn)
                                                router.push('/profile');
                                            else router.push('/login');
                                        }}
                                    />
                                </Box>
                            </>
                        )}

                        {/* ── Mobile Drawer ── */}
                        {sizes.sm && (
                            <Drawer
                                anchor="left"
                                onClose={handleDrawerClose}
                                open={showMenu}
                                transitionDuration={{ enter: 320, exit: 260 }}
                                variant="temporary"
                                style={{ zIndex: 10000 }}
                                PaperProps={{
                                    sx: {
                                        width: '100vw',
                                        maxWidth: '100%',
                                        background: 'hsl(var(--background))',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                    }}
                                >
                                    {/* ── Баннер ── */}
                                    <Box
                                        className="flex bg-tertiary-20 h-9 w-full justify-center items-center"
                                        sx={{ flexShrink: 0 }}
                                    >
                                        {ads?.length > 0 && ads[safeAdIndex] && (
                                            <span
                                                className={`text-primary-950 text-[13px] transition-opacity duration-500 ${fadeClass}`}
                                            >
                                                {
                                                    ads[safeAdIndex].names.find(
                                                        (item) => item.langCode === i18n.language
                                                    )?.name
                                                }
                                            </span>
                                        )}
                                    </Box>

                                    {/* ── Header: хаах + лого ── */}
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            width: '100%',
                                            px: 2,
                                            py: 4,
                                            borderBottom: '1px solid #f3f4f6',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {/* Зүүн товч — тогтмол 80px өргөн */}
                                        <Box sx={{ width: 80, flexShrink: 0 }}>
                                            {showSubMenu ? (
                                                <Box
                                                    onClick={handleDrawerClose}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        cursor: 'pointer',
                                                        color: '#374151',
                                                    }}
                                                >
                                                    <ChevronLeft size={18} />
                                                    <span style={{ fontSize: 13, fontWeight: 500 }}>
                                                        Буцах
                                                    </span>
                                                </Box>
                                            ) : (
                                                <Box
                                                    onClick={handleDrawerClose}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '50%',
                                                        background: '#f3f4f6',
                                                        cursor: 'pointer',
                                                        transition: 'background .15s',
                                                        '&:hover': { background: '#e5e7eb' },
                                                    }}
                                                >
                                                    <X size={16} color="#374151" />
                                                </Box>
                                            )}
                                        </Box>
                                        {/* Лого — яг голд */}
                                        <Box
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <Link href="/" passHref>
                                                <Image
                                                    width={120}
                                                    height={32}
                                                    src={
                                                        i18n.language === 'mn'
                                                            ? '/LogoFull.png'
                                                            : '/LogoFullEng.png'
                                                    }
                                                    alt="Gorgeous logo"
                                                    style={{ objectFit: 'contain' }}
                                                />
                                            </Link>
                                        </Box>
                                        {/* Баруун зай — тэнцвэртэй */}
                                        <Box sx={{ width: 80, flexShrink: 0 }} />
                                    </Box>

                                    {/* ── Sub цэсийн гарчиг ── */}
                                    {showSubMenu && subMenu && (
                                        <Box sx={{ px: 2.5, pt: 2.5, pb: 1, flexShrink: 0 }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: 'hsl(var(--primary))',
                                                    letterSpacing: '0.08em',
                                                    textTransform: 'uppercase',
                                                }}
                                            >
                                                {
                                                    subMenu.names.find(
                                                        (item) => item.langCode === i18n.language
                                                    )?.name
                                                }
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* ── Цэсүүд ── */}
                                    <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                                        {!showSubMenu ? (
                                            <>
                                                <MenuDrawerItem
                                                    menu={productMenus}
                                                    onClick={() => {
                                                        setSubMenu(productMenus);
                                                        setShowSubMenu(true);
                                                    }}
                                                />
                                                {state.system.userMenus?.map((m, ind) => (
                                                    <MenuDrawerItem
                                                        key={ind}
                                                        menu={m}
                                                        onClick={() => {
                                                            if (m.children && !m.children.length)
                                                                handleRoteClick(m);
                                                            else {
                                                                setSubMenu(m);
                                                                setShowSubMenu(true);
                                                            }
                                                        }}
                                                    />
                                                ))}
                                            </>
                                        ) : (
                                            subMenu?.children?.map((m, ind) => (
                                                <MenuDrawerSubItem
                                                    key={ind}
                                                    menu={m}
                                                    onClick={() => handleRoteClick(m)}
                                                />
                                            ))
                                        )}
                                    </Box>
                                </Box>
                            </Drawer>
                        )}
                    </Container>
                </Container>
            </div>
            {/* end sticky wrapper */}

            {/* ── Search Overlay ── */}
            <style>{`
                @keyframes overlayIn { from { opacity:0 } to { opacity:1 } }
                @keyframes boxIn { from { opacity:0; transform:translateY(-12px) } to { opacity:1; transform:translateY(0) } }
            `}</style>
            {showSearch && (
                <div
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowSearch(false);
                    }}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 99999,
                        background: 'rgba(0,0,0,0.55)',
                        backdropFilter: 'blur(6px)',
                        WebkitBackdropFilter: 'blur(6px)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        padding: '18vh 16px 0',
                        boxSizing: 'border-box',
                        animation: 'overlayIn .2s ease',
                    }}
                >
                    <form
                        onSubmit={handleSearchSubmit}
                        style={{
                            width: '100%',
                            maxWidth: 560,
                            animation: 'boxIn .22s cubic-bezier(.22,1,.36,1)',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'stretch',
                                background: 'hsl(var(--background))',
                                borderRadius: 14,
                                boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                                overflow: 'hidden',
                                border: '2px solid hsl(var(--primary) / 0.3)',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', marginLeft: 16 }}>
                                <Search
                                    size={18}
                                    style={{ flexShrink: 0, color: 'hsl(var(--primary))' }}
                                />
                            </div>
                            <input
                                autoFocus
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                placeholder={t('button.search') + '...'}
                                style={{
                                    all: 'unset',
                                    flex: 1,
                                    padding: '16px 12px',
                                    fontSize: 16,
                                    color: '#111827',
                                }}
                                onKeyDown={(e) => e.key === 'Escape' && setShowSearch(false)}
                            />
                            {searchValue && (
                                <button
                                    type="button"
                                    onClick={() => setSearchValue('')}
                                    style={{
                                        all: 'unset',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        background: '#f3f4f6',
                                        color: '#9ca3af',
                                        flexShrink: 0,
                                        margin: 'auto 8px',
                                    }}
                                >
                                    <X size={13} />
                                </button>
                            )}
                            <button
                                type="submit"
                                style={{
                                    all: 'unset',
                                    cursor: 'pointer',
                                    padding: '0 20px',
                                    background: 'hsl(var(--primary))',
                                    color: 'hsl(var(--primary-foreground))',
                                    fontSize: 14,
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.88')}
                                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                            >
                                {t('button.search')}
                            </button>
                        </div>
                        <p
                            style={{
                                margin: '10px 0 0',
                                fontSize: 11,
                                color: 'rgba(255,255,255,0.4)',
                                textAlign: 'center',
                            }}
                        >
                            ESC — хаах
                        </p>
                    </form>
                </div>
            )}
        </>
    );
};

export default Header;
